import { useMemo, useState } from "react";
import EnlaceTema from "../tema/EnlaceTema";
import { Box, Button, Card, CardContent, Collapse, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  LocalOffer as LocalOfferIcon,
  SwapHoriz as SwapHorizIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { InstanciaPaso } from "../../types/instanciaPaso";
import ProcesoCard from "./ProcesoCard";
import Stepper, { PasoStepper } from "../kit/Stepper";
import EstadoChip from "../kit/EstadoChip";
import Markdown from "../kit/Markdown";
import { analizarProceso, EntradaHistorial } from "../../utils/procesoEstudiante";
import { estiloDeInstancia } from "../../utils/estados";
import { tokens } from "../../theme";

// Íconos para descargar el/los documento(s) de un paso (informe y su corrección),
// pensados para ir junto al título del paso en el stepper.
function DescargasPaso({ inst }: { inst: InstanciaPaso }) {
  const docs = [
    { url: inst.documento, label: "Descargar informe" },
    { url: inst.documento_correccion, label: "Descargar corrección" },
  ].filter((d) => d.url);
  if (!docs.length) return null;
  return (
    <>
      {docs.map((d) => (
        <Tooltip key={d.label} title={d.label}>
          <IconButton
            size="small"
            component="a"
            href={d.url as string}
            target="_blank"
            rel="noreferrer"
            sx={{ color: tokens.marca.texto, p: 0.25 }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ))}
    </>
  );
}

// Resultado de un paso ya publicado, con la evaluación completa de cada profesor.
function ResultadoPaso({ inst }: { inst: InstanciaPaso }) {
  const conRespuestas = inst.evaluaciones.filter((e) => e.respuestas.length > 0);
  // La etiqueta cuando el resultado sale de una pregunta de opción; si no, la nota.
  const resultado = inst.resultado_categoria || inst.nota;
  if (!resultado && conRespuestas.length === 0) return null;
  return (
    <Box sx={{ mt: 0.75 }}>
      {resultado && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Resultado: {resultado}
        </Typography>
      )}
      {conRespuestas.length > 0 && (
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          {conRespuestas.map((e, i) => (
            <Box key={i} sx={{ borderLeft: "2px solid", borderColor: "divider", pl: 1.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }} display="block">
                {e.evaluador}
              </Typography>
              {e.respuestas.map((r, j) => (
                <Box key={j} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {r.pregunta}
                  </Typography>
                  {/* Los comentarios se escriben en Markdown (ver `EditorMarkdown`); lo demás, tal cual. */}
                  {r.tipo === "texto" ? (
                    <Markdown texto={String(r.valor)} />
                  ) : (
                    <Typography variant="body2">{String(r.valor)}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

// Una entrada del historial (paso finalizado o evento de tema) → ítem del stepper.
function aItemStepper(e: EntradaHistorial): PasoStepper {
  if (e.kind === "evento") {
    return {
      titulo: e.evento.tipo === "eleccion" ? "Tema elegido" : "Cambio de tema",
      subtitulo: <EnlaceTema id={e.evento.temaId} titulo={e.evento.tema} />,
      tono: "hito",
      icono: e.evento.tipo === "eleccion" ? <LocalOfferIcon sx={{ fontSize: 15 }} /> : <SwapHorizIcon sx={{ fontSize: 16 }} />,
    };
  }
  const inst = e.inst;
  const hayDocs = Boolean(inst.documento || inst.documento_correccion);
  // La etiqueta del resultado suele repetir el estado ("Aprobado · Aprobado"): en ese
  // caso el estado solo alcanza, y se muestra la etiqueta cuando dice algo más.
  const etiqueta = inst.resultado_categoria || (inst.nota ? `nota ${inst.nota}` : "");
  const resultado = etiqueta === inst.estado_display ? "" : etiqueta;
  return {
    titulo: inst.paso,
    subtitulo: `${inst.estado_display}${resultado ? ` · ${resultado}` : ""} · ${inst.ano} · ${inst.semestre_display}`,
    tono: inst.estado === "reprobado" ? "reprobado" : "completado",
    accion: hayDocs ? <DescargasPaso inst={inst} /> : undefined,
    contenido: <ResultadoPaso inst={inst} />,
  };
}

/**
 * Vista de "Procesos" para el estudiante: arriba, la acción que se le pide ahora;
 * abajo, el historial del ciclo activo como stepper (con la elección/cambio de
 * tema) y un resumen colapsable de los intentos de semestres anteriores.
 */
export default function ProcesosEstudiante({ instancias, soloLectura = false }: { instancias: InstanciaPaso[]; soloLectura?: boolean }) {
  const analisis = useMemo(() => analizarProceso(instancias), [instancias]);
  const items = useMemo(() => (analisis ? [...analisis.historial].reverse().map(aItemStepper) : []), [analisis]);
  const [verPrevios, setVerPrevios] = useState(false);
  if (!analisis) return null;

  const { acciones, proximos, intentosPrevios } = analisis;

  return (
    <Stack spacing={4}>
      {/* Acción / etapa actual */}
      <Box>
        <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
          {acciones.length ? (soloLectura ? "Etapa actual" : "Lo que debes hacer ahora") : "Estado del proceso"}
        </Typography>
        {/* Casi siempre una tarjeta; dos mientras hace la Vía Rápida, con la Propuesta
            Inicial todavía abierta: puede entregar en cualquiera. */}
        {acciones.length ? (
          <Stack spacing={2}>
            {acciones.map((it) => (
              <ProcesoCard key={it.id} it={it} showEstudiante={false} />
            ))}
          </Stack>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CheckCircleIcon sx={{ color: tokens.estado.exito }} />
              <Box>
                <Typography fontWeight={600}>{soloLectura ? "Sin acciones pendientes" : "No tienes acciones pendientes"}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {soloLectura ? "El proceso está al día." : "Tu proceso está al día."}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
        {proximos.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
            Luego: {proximos.map((p) => p.paso).join("  ·  ")}
          </Typography>
        )}
      </Box>

      {/* Historial del ciclo actual */}
      {items.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Historial
          </Typography>
          <Stepper items={items} />
        </Box>
      )}

      {/* Intentos de semestres anteriores */}
      {intentosPrevios.length > 0 && (
        <Box>
          <Button
            size="small"
            variant="text"
            onClick={() => setVerPrevios((v) => !v)}
            endIcon={<ExpandMoreIcon sx={{ transform: verPrevios ? "rotate(180deg)" : "none", transition: "transform .2s" }} />}
          >
            {verPrevios ? "Ocultar" : "Ver"} {intentosPrevios.length} {intentosPrevios.length === 1 ? "intento anterior" : "intentos anteriores"}
          </Button>
          <Collapse in={verPrevios}>
            <Stack spacing={1.25} sx={{ mt: 1.5, pl: 0.5 }}>
              {intentosPrevios.map((p) => (
                <Stack key={p.id} direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>
                    {p.ano} · {p.semestre_display}
                  </Typography>
                  <EstadoChip label={p.estado_display} {...estiloDeInstancia(p.estado)} denso />
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Stack>
  );
}
